import { z } from "zod";

// ─── Date presentation format (per-organization) ───────────────────────────
// The enum values are also valid date-fns patterns, so the web app can pass
// them straight to `format()`. Date-times append " HH:mm:ss".
export const dateFormatSchema = z.enum(["dd/MM/yyyy", "MM/dd/yyyy", "yyyy-MM-dd", "dd MMM yyyy"]);
export type DateFormat = z.infer<typeof dateFormatSchema>;
export const DEFAULT_DATE_FORMAT: DateFormat = "dd/MM/yyyy";
export const DATE_FORMAT_OPTIONS = dateFormatSchema.options;

export const updateOrganizationDateFormatInputSchema = z.object({
  dateFormat: dateFormatSchema,
});
export type UpdateOrganizationDateFormatInput = z.infer<
  typeof updateOrganizationDateFormatInputSchema
>;

export const updateOrganizationDateFormatResponseSchema = z.object({
  data: z.object({ dateFormat: dateFormatSchema }),
});

export const updateOrganizationInputSchema = z.object({
  name: z.string().min(1, "Organization name is required").max(100),
});

export type UpdateOrganizationInput = z.infer<typeof updateOrganizationInputSchema>;

export const organizationRoleSchema = z.enum(["owner", "admin", "member"]);
export type OrganizationRole = z.infer<typeof organizationRoleSchema>;

export const memberStatusSchema = z.enum(["active", "inactive"]);
export type MemberStatus = z.infer<typeof memberStatusSchema>;

export const inviteMemberInputSchema = z.object({
  email: z.string().email("Invalid email address"),
  role: organizationRoleSchema,
});
export type InviteMemberInput = z.infer<typeof inviteMemberInputSchema>;

export const updateMemberRoleInputSchema = z.object({
  role: organizationRoleSchema,
});
export type UpdateMemberRoleInput = z.infer<typeof updateMemberRoleInputSchema>;

export const organizationMemberSchema = z.object({
  id: z.string(),
  userId: z.string(),
  organizationId: z.string(),
  role: z.string(),
  status: memberStatusSchema.default("active"),
  createdAt: z.coerce.date(),
  user: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    image: z.string().nullable().optional(),
  }),
  // Login H2J (#700): conta criada pelo convite que ainda não entrou pelo SSO.
  pendingActivation: z.boolean().optional(),
});
export type OrganizationMember = z.infer<typeof organizationMemberSchema>;

export const organizationInvitationSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  email: z.string(),
  role: z.string(),
  status: z.string(),
  inviterId: z.string(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date(),
});
export type OrganizationInvitation = z.infer<typeof organizationInvitationSchema>;

export const organizationMembershipSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  role: z.string().min(1),
  dateFormat: dateFormatSchema.catch(DEFAULT_DATE_FORMAT),
});

export type OrganizationMembership = z.infer<typeof organizationMembershipSchema>;

export const listOrganizationMembershipsResponseSchema = z.object({
  data: z.array(organizationMembershipSchema),
});

export type ListOrganizationMembershipsResponse = z.infer<
  typeof listOrganizationMembershipsResponseSchema
>;

export const listOrganizationMembersResponseSchema = z.object({
  data: z.object({ members: z.array(organizationMemberSchema) }),
});
export type ListOrganizationMembersResponse = z.infer<typeof listOrganizationMembersResponseSchema>;

export const updateMemberStatusInputSchema = z.object({ status: memberStatusSchema });
export type UpdateMemberStatusInput = z.infer<typeof updateMemberStatusInputSchema>;

export const updateMemberStatusResponseSchema = z.object({
  data: z.object({ id: z.string(), status: memberStatusSchema }),
});
